/**
 * Reentry State — "How long has the user been gone, and what does that mean?"
 *
 * Computes a single classification from the user's recent activity (sessions,
 * recovery logs, nutrition entries). Drives:
 *   - The welcome-back banner on the dashboard
 *   - Mission generator filtering (skip baseline-relative missions when there's
 *     no recent baseline)
 *   - The smart return-to-training plan (when status === 'returning')
 *
 * Bands are deliberately conservative — a 4-day rest is normal training rhythm,
 * not "drifting." The drift threshold (7d) is one missed expected session;
 * the returning threshold (14d) is the point at which detraining starts to
 * matter measurably (per the fitness/fatigue model decay rates in
 * lib/fitnessFatigueModel.ts).
 */

import type { SessionLog } from './sessionStorage';
import type { RecoveryMetrics } from './types/recovery';
import type { NutritionEntry } from './types/nutrition';

export type ReentryStatus = 'fresh' | 'on-track' | 'drifting' | 'returning';

export interface ReentryState {
  status: ReentryStatus;
  /** Days since the most recent training session. `Infinity` if there's never been one. */
  daysSinceLastSession: number;
  /** Days since the most recent recovery log. `Infinity` if never logged. */
  daysSinceLastRecovery: number;
  /** Days since the most recent nutrition entry. `Infinity` if never logged. */
  daysSinceLastNutrition: number;
  /** ISO date of the most recent session (undefined if never trained). */
  lastSessionDate?: string;
  /** True when the user used to log nutrition but has stopped. */
  missedNutrition: boolean;
  /** True when the user used to log recovery but has stopped. */
  missedRecovery: boolean;
  /** A short, neutral phrase suitable for a banner header. */
  headline: string;
}

const DRIFT_DAYS = 7;
const RETURN_DAYS = 14;
const HABIT_GAP_DAYS = 5; // a habit is "missed" if used to log every ~3d but now 5+

const MS_PER_DAY = 86_400_000;

function daysAgo(date: Date | string | undefined | null): number {
  if (!date) return Infinity;
  const t = typeof date === 'string' ? Date.parse(date) : date.getTime();
  if (isNaN(t)) return Infinity;
  return Math.max(0, Math.floor((Date.now() - t) / MS_PER_DAY));
}

/**
 * Determine whether a habit (recovery, nutrition) has lapsed. A habit is
 * "missed" only when it was previously established — i.e., the user logged
 * at least 3 entries in the past 30 days, but the most recent entry is
 * more than HABIT_GAP_DAYS old.
 */
function isHabitMissed(entries: Array<{ date: Date | string }>, gapDays = HABIT_GAP_DAYS): boolean {
  if (entries.length < 3) return false; // never established the habit, can't have missed it
  const sorted = [...entries].sort((a, b) => {
    const ta = typeof a.date === 'string' ? Date.parse(a.date) : a.date.getTime();
    const tb = typeof b.date === 'string' ? Date.parse(b.date) : b.date.getTime();
    return tb - ta;
  });

  // Count entries in last 30 days
  const recent = sorted.filter(e => daysAgo(e.date) <= 30);
  if (recent.length < 3) return false;

  // Most recent entry older than threshold = habit lapsed
  return daysAgo(sorted[0].date) > gapDays;
}

/**
 * Build a ReentryState from raw activity data.
 *
 * All inputs are tolerant of empty arrays — a brand new user with zero data
 * gets `status: 'fresh'`.
 */
export function computeReentryState(input: {
  sessions: Array<{ date: Date | string }>;
  recovery: Array<{ date: Date | string }>;
  nutrition: Array<{ date: Date | string }>;
}): ReentryState {
  const sessions = input.sessions ?? [];
  const recovery = input.recovery ?? [];
  const nutrition = input.nutrition ?? [];

  // Find most recent of each
  const lastSession = sessions
    .map(s => (typeof s.date === 'string' ? new Date(s.date) : s.date))
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const daysSinceLastSession = lastSession ? daysAgo(lastSession) : Infinity;

  const lastRecovery = recovery
    .map(r => (typeof r.date === 'string' ? new Date(r.date) : r.date))
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0];
  const daysSinceLastRecovery = lastRecovery ? daysAgo(lastRecovery) : Infinity;

  const lastNutrition = nutrition
    .map(n => (typeof n.date === 'string' ? new Date(n.date) : n.date))
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0];
  const daysSinceLastNutrition = lastNutrition ? daysAgo(lastNutrition) : Infinity;

  const missedNutrition = isHabitMissed(nutrition);
  const missedRecovery = isHabitMissed(recovery);

  let status: ReentryStatus;
  if (sessions.length === 0) {
    status = 'fresh';
  } else if (daysSinceLastSession >= RETURN_DAYS) {
    status = 'returning';
  } else if (daysSinceLastSession >= DRIFT_DAYS) {
    status = 'drifting';
  } else {
    status = 'on-track';
  }

  let headline: string;
  switch (status) {
    case 'fresh':
      headline = 'Welcome — let’s set the baseline.';
      break;
    case 'on-track':
      headline = `On track — ${daysSinceLastSession} day${daysSinceLastSession === 1 ? '' : 's'} since last session.`;
      break;
    case 'drifting':
      headline = `${daysSinceLastSession} days since your last session.`;
      break;
    case 'returning':
      headline = `Welcome back. ${daysSinceLastSession} days off the books.`;
      break;
  }

  return {
    status,
    daysSinceLastSession,
    daysSinceLastRecovery,
    daysSinceLastNutrition,
    lastSessionDate: lastSession?.toISOString(),
    missedNutrition,
    missedRecovery,
    headline,
  };
}

/**
 * Convenience: build the state from full SessionLog/RecoveryMetrics/NutritionEntry
 * arrays as returned by the storage layers. Just normalizes the date field
 * shapes before delegating.
 */
export function reentryStateFromHistory(
  sessions: SessionLog[],
  recovery: RecoveryMetrics[],
  nutrition: NutritionEntry[],
): ReentryState {
  return computeReentryState({
    sessions: sessions.map(s => ({ date: s.date })),
    recovery: recovery.map(r => ({ date: r.date })),
    nutrition: nutrition.map(n => ({ date: (n as any).date ?? (n as any).loggedAt ?? (n as any).createdAt })),
  });
}
